'use client';

import { useFilingsList, useGetAllComplianceItems } from '@commenda/api';
import { SyntheticComplianceStatus } from '@commenda/types/src/compliance-calendar/compliances/types';
import type { UnifiedFiling } from '@commenda/types/src/compliance-calendar/compliances/unified-filing';
import {
  mapComplianceItemToUnifiedFiling,
  mapSalesTaxFilingToUnifiedFiling,
} from '@commenda/utils';
import { useEffect, useMemo, useState } from 'react';

export type FilingsTab = 'UPCOMING' | 'OVERDUE' | 'COMPLETED' | 'ARCHIVED';

export type FilingTabItem = {
  id: FilingsTab;
  label: string;
  badge: { count: number; variant: string };
  compliances: UnifiedFiling[];
};

type UseFilingTabsProps = {
  businessEntityId: number;
  year?: number;
  includeIndirectTaxFilings?: boolean;
  includeHiddenComplianceTypes?: boolean;
  enabled?: boolean;
};

const useFilingTabs = ({
  businessEntityId,
  year,
  includeIndirectTaxFilings = false,
  includeHiddenComplianceTypes = false,
  enabled = true,
}: UseFilingTabsProps) => {
  const {
    data: compliancesData,
    error: compliancesError,
    mutate: refreshCompliances,
  } = useGetAllComplianceItems(undefined, {
    corporationId: businessEntityId,
    includeCAFirm: true,
    includeAssignees: true,
    includeHiddenComplianceTypes,
    enabled,
  });

  const {
    filings: salesTaxFilings,
    isLoading: isSalesTaxLoading,
    error: salesTaxError,
    mutate: refreshSalesTaxFilings,
  } = useFilingsList({
    corporationId: businessEntityId,
    limit: 200,
    includeServiceRequest: true,
    includeAssignee: true,
    enabled: enabled && includeIndirectTaxFilings,
  });

  if (compliancesError) console.error(compliancesError);
  if (salesTaxError) console.error(salesTaxError);

  const [internalSelectedYear, setInternalSelectedYear] = useState<
    number | null
  >(null);

  const selectedYear = year !== undefined ? year : internalSelectedYear;
  const setSelectedYear = setInternalSelectedYear;

  const allUnifiedFilings = useMemo(() => {
    const complianceItems = (compliancesData?.compliances || []).map(
      mapComplianceItemToUnifiedFiling
    );

    if (!includeIndirectTaxFilings) return complianceItems;

    const salesTaxItems = (salesTaxFilings || []).map(
      mapSalesTaxFilingToUnifiedFiling
    );
    return [...complianceItems, ...salesTaxItems];
  }, [compliancesData, salesTaxFilings, includeIndirectTaxFilings]);

  const hasSalesTaxFilings = useMemo(
    () => (salesTaxFilings?.length ?? 0) > 0,
    [salesTaxFilings]
  );

  const availableYears = useMemo(() => {
    const yearsSet = new Set<number>();
    allUnifiedFilings.forEach((filing) => {
      if (filing.effectiveYear) {
        yearsSet.add(filing.effectiveYear);
      }
    });
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [allUnifiedFilings]);

  const [filingTabs, setFilingTabs] = useState<FilingTabItem[]>([
    {
      id: 'UPCOMING',
      label: 'Upcoming',
      badge: { count: 0, variant: 'gray' },
      compliances: [],
    },
    {
      id: 'OVERDUE',
      label: 'Overdue',
      badge: { count: 0, variant: 'error' },
      compliances: [],
    },
    {
      id: 'COMPLETED',
      label: 'Completed',
      badge: { count: 0, variant: 'gray' },
      compliances: [],
    },
    {
      id: 'ARCHIVED',
      label: 'Archived',
      badge: { count: 0, variant: 'gray' },
      compliances: [],
    },
  ]);

  useEffect(() => {
    const filings = selectedYear
      ? allUnifiedFilings.filter(
          (filing) => filing.effectiveYear === selectedYear
        )
      : allUnifiedFilings;

    setFilingTabs((prevTabs) => {
      const newTabs = prevTabs.map((tab) => ({
        ...tab,
        compliances: [] as UnifiedFiling[],
        badge: { ...tab.badge, count: 0 },
      }));

      const upcomingIndex = newTabs.findIndex((tab) => tab.id === 'UPCOMING');
      const overdueIndex = newTabs.findIndex((tab) => tab.id === 'OVERDUE');
      const completedIndex = newTabs.findIndex((tab) => tab.id === 'COMPLETED');
      const archivedIndex = newTabs.findIndex((tab) => tab.id === 'ARCHIVED');

      filings.forEach((filing) => {
        const status = filing.syntheticStatus;

        if (status === SyntheticComplianceStatus.ARCHIVED) {
          newTabs[archivedIndex].compliances.push(filing);
          newTabs[archivedIndex].badge.count =
            newTabs[archivedIndex].compliances.length;
        } else if (
          status === SyntheticComplianceStatus.FILED ||
          status === SyntheticComplianceStatus.COMPLETED
        ) {
          newTabs[completedIndex].compliances.push(filing);
          newTabs[completedIndex].badge.count =
            newTabs[completedIndex].compliances.length;
        } else if (status === SyntheticComplianceStatus.OVERDUE) {
          newTabs[overdueIndex].compliances.push(filing);
          newTabs[overdueIndex].badge.count =
            newTabs[overdueIndex].compliances.length;
        } else {
          newTabs[upcomingIndex].compliances.push(filing);
          newTabs[upcomingIndex].badge.count =
            newTabs[upcomingIndex].compliances.length;
        }
      });

      return newTabs;
    });
  }, [allUnifiedFilings, selectedYear]);

  const refreshAll = useMemo(() => {
    return () => {
      refreshCompliances();
      if (includeIndirectTaxFilings) {
        refreshSalesTaxFilings();
      }
    };
  }, [refreshCompliances, refreshSalesTaxFilings, includeIndirectTaxFilings]);

  const isCompliancesLoading = !compliancesData && !compliancesError;
  const isLoading = includeIndirectTaxFilings
    ? isCompliancesLoading || isSalesTaxLoading
    : isCompliancesLoading;

  return {
    compliancesError: compliancesError || salesTaxError,
    isLoading,
    filingTabs,
    refreshCompliances: refreshAll,
    selectedYear,
    setSelectedYear,
    availableYears,
    hasSalesTaxFilings,
  };
};

export { useFilingTabs };
